import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Ban, Landmark, Plus, Search } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inr, fmtDate, today, PAYMENT_MODES, titleCase } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { useCanEdit, useHasModule } from "@/context/AuthContext";
import { CardList, DataCard, TableWrap } from "@/components/DataCards";
import Pagination, { usePagedList } from "@/components/Pagination";

export default function Payments() {
  const [params, setParams] = useSearchParams();
  const canEdit = useCanEdit();
  const hasBanking = useHasModule("banking");
  const [rows, setRows] = useState([]);
  const [parties, setParties] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [openDocs, setOpenDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [manual, setManual] = useState(false);
  const [allocs, setAllocs] = useState({});
  const [form, setForm] = useState({
    payment_type: "PAYMENT_IN", party_id: "", payment_date: today(), amount: "",
    payment_mode: "UPI", account_id: "", reference_number: "", notes: "",
  });

  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [range, setRange] = useState({ from_date: "", to_date: "" });

  const query = useMemo(() => ({
    q: q || undefined,
    type: typeFilter === "ALL" ? undefined : typeFilter,
    from_date: range.from_date || undefined,
    to_date: range.to_date || undefined,
  }), [q, typeFilter, range]);

  const { rows: pagedRows, total, page, setPage, size, setSize, loading, reload } =
    usePagedList("/payments", query);
  const load = reload;

  useEffect(() => { setRows(pagedRows); }, [pagedRows]);

  useEffect(() => {
    api.get("/parties").then(({ data }) => setParties(data));
  }, []);

  // With banking on, the money has to land somewhere — preselect the default
  // account so the common case is still one click.
  useEffect(() => {
    if (!hasBanking) return;
    api.get("/banking/accounts").then(({ data }) => {
      setAccounts(data);
      const preferred = data.find((a) => a.is_default) || data[0];
      if (preferred) setForm((f) => (f.account_id ? f : { ...f, account_id: preferred.id }));
    }).catch(() => {});
  }, [hasBanking]);

  useEffect(() => {
    if (params.get("new")) {
      setForm((f) => ({
        ...f,
        party_id: params.get("party_id") || "",
        amount: params.get("amount") || "",
      }));
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (!form.party_id) { setOpenDocs([]); return; }
    api.get(`/parties/${form.party_id}/open-documents`).then(({ data }) => setOpenDocs(data));
  }, [form.party_id]);

  const allocatedTotal = useMemo(
    () => Object.values(allocs).reduce((s, v) => s + (Number(v) || 0), 0),
    [allocs]
  );

  const save = async () => {
    try {
      if (hasBanking && !form.account_id) {
        toast.error("Choose the bank or cash account for this payment");
        return;
      }
      const payload = {
        ...form,
        account_id: hasBanking ? form.account_id : undefined,
        amount: Number(form.amount) || 0,
        auto_allocate: !manual,
        allocations: manual
          ? Object.entries(allocs)
              .filter(([, v]) => Number(v) > 0)
              .map(([document_id, amount]) => ({ document_id, amount: Number(amount) }))
          : [],
      };
      const { data } = await api.post("/payments", payload);
      toast.success(`Payment ${data.payment_number} recorded`);
      setOpen(false);
      setParams({});
      setAllocs({});
      setManual(false);
      setForm((f) => ({ ...f, amount: "", reference_number: "", notes: "" }));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const voidPayment = async (p) => {
    try {
      await api.post(`/payments/${p.id}/void`);
      toast.success("Payment voided, invoices restored to outstanding");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const totals = useMemo(
    () => ({
      inAmt: rows.filter((r) => r.payment_type === "PAYMENT_IN" && r.status === "ACTIVE").reduce((s, r) => s + r.amount, 0),
      outAmt: rows.filter((r) => r.payment_type === "PAYMENT_OUT" && r.status === "ACTIVE").reduce((s, r) => s + r.amount, 0),
    }),
    [rows]
  );

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Payments</h1>
          <p className="text-sm text-slate-500 mt-1">
            Received <span className="font-mono text-emerald-700">{inr(totals.inAmt)}</span> · paid out{" "}
            <span className="font-mono text-rose-700">{inr(totals.outAmt)}</span>
          </p>
        </div>
        {canEdit && (
          <Button data-testid="new-payment-btn" onClick={() => setOpen(true)} className="gap-1.5 rounded-full bg-blue-600 hover:bg-blue-700">
            <Plus size={16} /> Record Payment
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="relative w-full sm:w-64">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="payments-search" className="h-9 pl-8" value={q}
            onChange={(e) => setQ(e.target.value)} placeholder="Number, party or reference" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-9 w-full sm:w-44" data-testid="payments-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All payments</SelectItem>
            <SelectItem value="PAYMENT_IN">Received</SelectItem>
            <SelectItem value="PAYMENT_OUT">Paid out</SelectItem>
          </SelectContent>
        </Select>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase text-slate-500">From</Label>
          <Input type="date" className="h-9 w-36" data-testid="payments-from" value={range.from_date}
            onChange={(e) => setRange((r) => ({ ...r, from_date: e.target.value }))} />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase text-slate-500">To</Label>
          <Input type="date" className="h-9 w-36" data-testid="payments-to" value={range.to_date}
            onChange={(e) => setRange((r) => ({ ...r, to_date: e.target.value }))} />
        </div>
      </div>

      <TableWrap>
        <table className="w-full text-sm" data-testid="payments-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">Number</th>
              <th className="px-3 py-2.5 text-left font-semibold">Date</th>
              <th className="px-3 py-2.5 text-left font-semibold">Party</th>
              <th className="px-3 py-2.5 text-left font-semibold">Mode</th>
              {hasBanking && <th className="px-3 py-2.5 text-left font-semibold">Account</th>}
              <th className="px-3 py-2.5 text-left font-semibold">Reference</th>
              <th className="px-3 py-2.5 text-right font-semibold">Amount</th>
              <th className="px-3 py-2.5 text-right font-semibold">Allocated</th>
              <th className="px-4 py-2.5 text-right font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((p) => {
              const allocated = (p.allocations || []).reduce((s, a) => s + a.amount, 0);
              return (
                <tr key={p.id} className="hover:bg-slate-50 transition-colors duration-150" data-testid={`payment-row-${p.id}`}>
                  <td className="px-4 py-2.5 font-mono text-xs font-semibold">{p.payment_number}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">{fmtDate(p.payment_date)}</td>
                  <td className="px-3 py-2.5">{p.party_name}</td>
                  <td className="px-3 py-2.5 text-xs">{titleCase(p.payment_mode)}</td>
                  {hasBanking && (
                    <td className="px-3 py-2.5 text-xs text-slate-600">
                      {p.account_name || <span className="text-amber-700">Unassigned</span>}
                    </td>
                  )}
                  <td className="px-3 py-2.5 font-mono text-[11px] text-slate-500">{p.reference_number || "—"}</td>
                  <td className={`px-3 py-2.5 text-right font-mono tabular-nums font-semibold ${
                    p.payment_type === "PAYMENT_IN" ? "text-emerald-700" : "text-rose-700"}`}>
                    {p.payment_type === "PAYMENT_IN" ? "+" : "−"}{inr(p.amount)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs">
                    {inr(allocated)}
                    {p.unallocated_amount > 0.01 && (
                      <div className="text-[10px] text-amber-700">on account {inr(p.unallocated_amount)}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <StatusBadge status={p.status === "ACTIVE" ? "PAID" : "VOID"} label={titleCase(p.status)} />
                    {canEdit && p.status === "ACTIVE" && (
                      <button className="ml-2 text-slate-400 hover:text-rose-600 transition-colors duration-150"
                        title="Void payment" data-testid={`payment-void-${p.id}`} onClick={() => voidPayment(p)}>
                        <Ban size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!rows.length && (
              <tr><td colSpan={hasBanking ? 9 : 8} className="px-4 py-14 text-center text-sm text-slate-500">No payments recorded yet</td></tr>
            )}
          </tbody>
        </table>
      </TableWrap>

      <CardList empty="No payments recorded yet" testid="payments-cards">
        {rows.map((p) => {
          const allocated = (p.allocations || []).reduce((sum, a) => sum + a.amount, 0);
          return (
            <DataCard
              key={p.id}
              testid={`payment-card-${p.id}`}
              title={p.payment_number}
              subtitle={p.party_name}
              right={
                <>
                  <div className={`font-mono text-sm font-bold tabular-nums ${
                    p.payment_type === "PAYMENT_IN" ? "text-emerald-700" : "text-rose-700"}`}>
                    {p.payment_type === "PAYMENT_IN" ? "+" : "−"}{inr(p.amount)}
                  </div>
                  <div className="mt-1">
                    <StatusBadge status={p.status === "ACTIVE" ? "PAID" : "VOID"} label={titleCase(p.status)} />
                  </div>
                </>
              }
              rows={[
                ["Date", fmtDate(p.payment_date)],
                ["Mode", titleCase(p.payment_mode)],
                ...(hasBanking ? [["Account", p.account_name || "Unassigned"]] : []),
                p.reference_number ? ["Reference", p.reference_number] : null,
                ["Allocated", p.unallocated_amount > 0.01
                  ? `${inr(allocated)} · on account ${inr(p.unallocated_amount)}`
                  : inr(allocated)],
              ]}
              actions={
                canEdit && p.status === "ACTIVE" && (
                  <button className="inline-flex items-center gap-1 text-xs font-medium text-rose-600"
                    data-testid={`payment-void-m-${p.id}`} onClick={() => voidPayment(p)}>
                    <Ban size={13} /> Void payment
                  </button>
                )
              }
            />
          );
        })}
      </CardList>

      <Pagination page={page} setPage={setPage} size={size} setSize={setSize}
        total={total} loading={loading} testid="payments-pagination" />

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setParams({}); }}>
        <DialogContent className="max-w-xl" data-testid="payment-dialog">
          <DialogHeader><DialogTitle>Record payment</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Direction</Label>
              <Select value={form.payment_type} onValueChange={(v) => setForm((f) => ({ ...f, payment_type: v }))}>
                <SelectTrigger data-testid="payment-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PAYMENT_IN">Payment In (received)</SelectItem>
                  <SelectItem value="PAYMENT_OUT">Payment Out (paid)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Date</Label>
              <Input type="date" data-testid="payment-date-input" value={form.payment_date}
                onChange={(e) => setForm((f) => ({ ...f, payment_date: e.target.value }))} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Party</Label>
              <Select value={form.party_id} onValueChange={(v) => setForm((f) => ({ ...f, party_id: v }))}>
                <SelectTrigger data-testid="payment-party-select"><SelectValue placeholder="Select party" /></SelectTrigger>
                <SelectContent>
                  {parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.display_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Amount</Label>
              <Input data-testid="payment-amount-input" className="font-mono text-right" value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Mode</Label>
              <Select value={form.payment_mode} onValueChange={(v) => setForm((f) => ({ ...f, payment_mode: v }))}>
                <SelectTrigger data-testid="payment-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_MODES.map((m) => <SelectItem key={m} value={m}>{titleCase(m)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {hasBanking && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs">
                  {form.payment_type === "PAYMENT_IN" ? "Received into" : "Paid from"}
                </Label>
                {accounts.length ? (
                  <Select value={form.account_id}
                    onValueChange={(v) => setForm((f) => ({ ...f, account_id: v }))}>
                    <SelectTrigger data-testid="payment-account-select">
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      {accounts.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name} · {inr(a.current_balance)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    <Landmark size={14} />
                    No accounts yet — add one under Banking before recording payments.
                  </div>
                )}
                {form.payment_mode === "CHEQUE" && (
                  <p className="text-[11px] text-slate-500">
                    A cheque only moves this balance once you mark it cleared.
                  </p>
                )}
              </div>
            )}
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Reference (UTR / cheque no.)</Label>
              <Input data-testid="payment-reference-input" className="font-mono" value={form.reference_number}
                onChange={(e) => setForm((f) => ({ ...f, reference_number: e.target.value }))} />
            </div>

            {!!openDocs.length && (
              <div className="sm:col-span-2 rounded-md border border-slate-200">
                <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                    Open invoices ({openDocs.length})
                  </div>
                  <button className="text-xs text-blue-700 hover:text-blue-900 transition-colors duration-150"
                    data-testid="toggle-manual-allocation" onClick={() => setManual((m) => !m)}>
                    {manual ? "Use FIFO auto-allocate" : "Allocate manually"}
                  </button>
                </div>
                <div className="max-h-44 overflow-y-auto divide-y divide-slate-100">
                  {openDocs.map((d) => (
                    <div key={d.id} className="flex items-center justify-between gap-3 px-3 py-2 text-xs" data-testid={`open-doc-${d.id}`}>
                      <div>
                        <div className="font-mono font-semibold">{d.document_number}</div>
                        <div className="text-slate-500">{fmtDate(d.document_date)} · due {fmtDate(d.due_date)}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-rose-700">{inr(d.balance_due)}</span>
                        {manual && (
                          <Input className="h-7 w-24 text-right font-mono text-xs" data-testid={`alloc-input-${d.id}`}
                            value={allocs[d.id] || ""} onChange={(e) => setAllocs((a) => ({ ...a, [d.id]: e.target.value }))} />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-3 py-2 text-[11px] text-slate-500 border-t border-slate-200">
                  {manual
                    ? `Allocated ${inr(allocatedTotal)} of ${inr(Number(form.amount) || 0)}`
                    : "Oldest invoice first (FIFO). Any excess is held on account."}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="payment-cancel">Cancel</Button>
            <Button onClick={save} data-testid="payment-save" className="bg-blue-600 hover:bg-blue-700">Save payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
